import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { trim, trimStart } from 'lodash';
import {
	SdfAlert,
	SdfBusyIndicator,
	SdfInput,
	SdfTextarea
} from '@synerg/react-components';
import { descriptionLength } from '../../../common';

function StepOne({ state, control, applicationNameList, loadingAppNames }) {
	const { projectDescription = '' } = state;
	const [numberOfEndLineCharacter, setNumberOfEndLineCharacter] = useState(0);
	const [projectDescriptionLength, setProjectDescriptionLength] = useState(0);

	const validatorsForName = {
		tooShort: value => trim(value).length >= 3 ||
				'Project name too short please use more than 3 characters',
		tooLong: value => trim(value).length <= 40 ||
				'Project name too long please use 40 characters or less',
		invalidCharacters: value => !RegExp(/[^A-Za-z0-9-. ]/).test(value) ||
				'Valid characters permitted are A-Z, a-z, 0-9, period (.), dash (-), and spaces',
		nameTaken: value => {
			const pnt = trim(value);
			if (applicationNameList) {
				return !(applicationNameList.some(name => name.replace(/(_DEV|_delete_DEV)? - (Application|Data connector)/ig, '').toLowerCase() ===
						pnt.replace(/(_DEV|_delete_DEV)? - (Application|Data connector)/ig,
								'').toLowerCase())) || 'App name is taken';
			}
		},
	};

	const validatorsForDescription = {
		tooLong: value => trim(value).length <= 256 ||
				'Project description too long please use 256 characters or less',
		invalidCharacters: value => !(/[^A-Za-z0-9-. \n]/.test(trim(value))) ||
				'Valid characters permitted are A-Z, a-z, 0-9, period (.), dash (-), and spaces',
	};

	const updateProjectDescriptionLength = (value) => {
		if (value && value.length > 0) {
			const {endLineChar, lengthWithoutEndlineChar} = descriptionLength(value);
			setNumberOfEndLineCharacter(endLineChar);
			setProjectDescriptionLength(lengthWithoutEndlineChar);
		} else {
			setProjectDescriptionLength(0);
		}
	};

	const ShowError = ({message}) => {
		return (
			<SdfAlert	slot="alert" size="sm" status="error" type="inline">
				{message}
			</SdfAlert>
		);
	};

	return (<div>
		<div className="px-6 py-8">
			<div className="text-heading-05 font-bold pb-4">Project details</div>
			<div className="text-body-02">
				Create a project to start your integration journey with ADP. By answering a
				few questions we can provide you with milestones and supply you with
				the API credentials you need to get started.
			</div>

			<section className="w-full mt-10">
				<Controller
					name="projectName"
					control={control}
					rules={{
						validate: validatorsForName,
					}}
					render={({ field: {value, onChange}, fieldState: {error} }) => (
						<div className="flex items-center justify-center h-full flex-row gap-4 relative">
							<SdfInput
								label="Project Name"
								name="projectName"
								className="mt-5 w-full order-1"
								onSdfInput={(e) => {
									onChange(trimStart(e.target.value));
								}}
								value={value}
								state={error ? 'error' : 'normal'}
								maxlength={40}
							>
								{error && error.message && <ShowError message={error.message} />}
							</SdfInput>
							{loadingAppNames &&
								<section data-cvs-id="36" className="order-2 mt-10">
									<SdfBusyIndicator data-cvs-id="37" id="demo-component" size="sm" />
								</section>}
						</div>
					)}
				/>
				<span className="text-body-short-01 pt-1">Don't worry, you can always change this later. Be sure to include at least 3 characters.</span>
			</section>

			<section className="w-full mt-6">
				<Controller
					name="projectDescription"
					control={control}
					rules={{
						validate: validatorsForDescription, // required: "Project description is required"
					}}
					render={({ field: {value, onChange}, fieldState: {error} }) => (
						<SdfTextarea
							name="projectDescription"
							label="Project description (optional)"
							className="mt-5"
							rows={4}
							onSdfInput={(e) => {
								onChange(trimStart(e.target.value));
								updateProjectDescriptionLength(e.target.value);
							}}
							value={value}
							state={error ? 'error' : 'normal'}
							maxlength={256 + numberOfEndLineCharacter}
							resize='none'
							>
								{error && error.message && <ShowError message={error.message} />}
						</SdfTextarea>
					)}
				/>
				<div className="flex flex-row justify-between mb-2">
					<span className="text-body-short-01 order-1 pt-1">
						Tell us more about your project. This won’t be seen by clients. You can always change it later.
					</span>
					<div className="text-right order-2 font-semibold">
						{projectDescription.length || projectDescriptionLength || 0}/256
					</div>
				</div>
			</section>

			<section className="w-full mt-8">
				<sdf-alert status="info" type="banner" size="sm">
					<span>This project and its credentials will be generated for a development environment by default.</span>
				</sdf-alert>
			</section>
		</div>
	</div>);
}

export default StepOne;
